import { useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { useCallback, useEffect, useState } from "react";

import {
  applyInvalidations,
  INVALIDATES,
} from "@/components/application/merchant-tasks/api/invalidates";
import { merchantFlowService } from "@/components/application/merchant-tasks/api/merchantFlowService";
import type { ContractPreviewModalProps } from "@/components/application/merchant-tasks/send/ContractPreviewModal";
import { planDefaultOnboarding } from "@/components/application/merchant-tasks/send/defaultOnboarding";
import { hasMainContract } from "@/components/application/merchant-tasks/send/hasMainContract";
import { useContractPreview } from "@/components/application/merchant-tasks/send/useContractPreview";
import { classifySendFailure } from "@/components/application/merchant-tasks/send/useSendFlow";
import { approvalOnboardingDecision } from "@/components/underwriting/approvalOnboarding";
import { useAssessmentByBorrowerQuery, useOrganizationSettingsQuery } from "@/hooks/queries";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { handleApiError } from "@/services/api";

const NOT_SENT = "The onboarding flow was not sent.";

/**
 * Approve + Send Contract for a chat approve, as the dashboard's Confirm Approve offers it.
 *
 * The chat approve mirrors the applications UI (FunderAnalysisDashboard approve dialog,
 * approvalOnboarding.ts); change both together. The chat counts as the AI Analysis entry
 * (`startDefaultOnboarding`), so the rule is the dashboard's own: the button shows only when the
 * application is not approved yet, the org sends contracts on approve and the user may send
 * Merchant Tasks; with it, the dialog shows the defaults notice. A card whose server does not
 * offer the send (`offered` false: its `onboarding` block) never shows it either, nor does a card on
 * the picked fake model (`onFake`, useOnFake).
 *
 * `send` is the dashboard's step after its save, run once the approve call has run (the call is
 * the save): plan the default onboarding again, then preview the contract or send the flow, on
 * the same endpoints. The two change together. `sent` once the flow went out, as the run never
 * hears of it.
 */
export function useApprovalSend(
  applicationId: number,
  dialogOpen: boolean,
  offered?: boolean,
  onFake = false
) {
  const queryClient = useQueryClient();
  const { canSendMerchantTasks } = useCurrentUser();
  const { data: settings, isError: settingsError } = useOrganizationSettingsQuery();
  const { data: assessment } = useAssessmentByBorrowerQuery(applicationId);
  // Null until settings load. A failed read keeps the default (send), as the dashboard does.
  const autoSendContractsEnabled = settings
    ? settings.auto_send_contracts_enabled !== false
    : settingsError
      ? true
      : null;
  const decision = approvalOnboardingDecision({
    startDefaultOnboarding: true,
    alreadyAccepted: assessment?.accepted === true,
    autoSendContractsEnabled,
    canSendMerchantTasks: canSendMerchantTasks(settings),
  });
  // On the picked fake nothing is sent, so the dialog reads no template defaults either.
  const showSendContractOption = decision === "dual" && offered !== false && !onFake;

  const [notice, setNotice] = useState<string | null>(null);
  const [noticeError, setNoticeError] = useState(false);
  const [sendBlocked, setSendBlocked] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const contractPreview = useContractPreview(applicationId);
  const startContractPreview = contractPreview.start;

  useEffect(() => {
    setNotice(null);
    setNoticeError(false);
    setSendBlocked(false);
    if (!dialogOpen || !showSendContractOption) return;
    let cancelled = false;
    merchantFlowService
      .defaults(applicationId, "onboarding")
      .then((evaluation) => {
        if (cancelled) return;
        const plan = planDefaultOnboarding(evaluation);
        setNotice(plan.notice);
        setNoticeError(plan.error);
        setSendBlocked(plan.request === null);
      })
      .catch(() => {
        if (!cancelled) setNotice("");
      });
    return () => {
      cancelled = true;
    };
  }, [dialogOpen, showSendContractOption, applicationId]);

  const send = useCallback(async () => {
    setError(null);
    try {
      // The approve saved the offer. Plan again so contract terms that come from that
      // assessment are present, then preview or send.
      const evaluation = await merchantFlowService.defaults(applicationId, "onboarding");
      const plan = planDefaultOnboarding(evaluation);
      if (plan.request && hasMainContract(evaluation)) {
        await startContractPreview(plan.request);
      } else if (plan.request) {
        await merchantFlowService.send(applicationId, plan.request);
        applyInvalidations(queryClient, INVALIDATES.send(applicationId));
        setSent(true);
      } else if (plan.error) {
        setError(plan.notice);
      }
    } catch (err) {
      setError(isAxiosError(err) ? handleApiError(err) : NOT_SENT);
    }
  }, [applicationId, queryClient, startContractPreview]);

  const discardPreview = async (reason: string) => {
    await contractPreview.discard();
    setError(reason);
  };

  const confirmPreview = async () => {
    setError(null);
    try {
      await contractPreview.confirm((request) => merchantFlowService.send(applicationId, request));
      applyInvalidations(queryClient, INVALIDATES.send(applicationId));
      contractPreview.release();
      setSent(true);
    } catch (err) {
      const failure = classifySendFailure(err);
      // Staff (or a reachable DocuSeal) can still fix the preview: keep it open.
      if (failure.kind === "preview") {
        contractPreview.showError(failure.message);
        return;
      }
      await discardPreview(isAxiosError(err) ? handleApiError(err) : NOT_SENT);
    }
  };

  const previewModal: ContractPreviewModalProps = {
    open: contractPreview.preview != null,
    preview: contractPreview.preview,
    confirming: contractPreview.busy,
    completed: contractPreview.completed,
    error: contractPreview.actionError,
    onConfirm: () => void confirmPreview(),
    onCancel: () => void discardPreview("Contract preview cancelled."),
    onCompleted: contractPreview.markCompleted,
    onRefresh: () => void contractPreview.refresh(),
    onEditAgain: () => void contractPreview.editAgain(),
  };

  return {
    showSendContractOption,
    onboardingNotice: notice,
    onboardingNoticeError: noticeError,
    /**
     * The notice says no contract would go out (it cannot be sent, or a flow is open already):
     * pressing Approve + Send Contract would only approve.
     */
    sendBlocked,
    confirmReady: decision !== "hold" && (!showSendContractOption || notice != null),
    /** Why the contract did not go out (the approve itself stands). */
    error,
    /** The contract went out (sent, or confirmed from its preview). */
    sent,
    send,
    previewModal,
  };
}
